import React, { useState, useEffect, useCallback } from 'react';

function App() {
  const [grid, setGrid] = useState([]);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [gameId, setGameId] = useState('');
  const [moveCount, setMoveCount] = useState(0);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [showNFTModal, setShowNFTModal] = useState(false);
  const [isMintingNFT, setIsMintingNFT] = useState(false);
  const [mintedNFTs, setMintedNFTs] = useState([]);
  const [finalScore, setFinalScore] = useState(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);

  const BACKEND_WALLET = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb';
  
  const TEMPO_TESTNET_CHAIN_ID = '0xA5DD';
  const TEMPO_TESTNET_CONFIG = {
    chainId: TEMPO_TESTNET_CHAIN_ID,
    chainName: 'Tempo Testnet (Andantino)',
    nativeCurrency: {
      name: 'AlphaUSD',
      symbol: 'AlphaUSD',
      decimals: 18
    },
    rpcUrls: ['https://rpc.testnet.tempo.xyz'],
    blockExplorerUrls: ['https://explore.tempo.xyz']
  };

  const initGrid = useCallback(() => {
    const newGrid = Array(4).fill(null).map(() => Array(4).fill(0));
    addRandomTile(newGrid);
    addRandomTile(newGrid);
    return newGrid;
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('best2048');
    if (saved) setBestScore(parseInt(saved));
    setGrid(initGrid());
    setGameId(Date.now().toString());
    checkWalletConnection();
    
    const savedNFTs = localStorage.getItem('minted2048NFTs');
    if (savedNFTs) setMintedNFTs(JSON.parse(savedNFTs));
  }, [initGrid]);

  const checkWalletConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
      }
    }
  };

  const switchToTempoTestnet = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: TEMPO_TESTNET_CHAIN_ID }],
      });
    } catch (error) {
      if (error.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [TEMPO_TESTNET_CONFIG],
          });
        } catch (addError) {
          console.error('Error adding Tempo Testnet:', addError);
          alert('Failed to add Tempo Testnet to your wallet');
        }
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        setIsConnecting(true);
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setWalletAddress(accounts[0]);
        await switchToTempoTestnet();
        
        window.ethereum.on('accountsChanged', (accounts) => {
          if (accounts.length > 0) {
            setWalletAddress(accounts[0]);
          } else {
            setWalletAddress('');
          }
        });
      } catch (error) {
        console.error('Error connecting wallet:', error);
        alert('Failed to connect wallet. Please try again.');
      } finally {
        setIsConnecting(false);
      }
    } else {
      alert('Install MetaMask to connect your wallet (optional - game works without it!)');
    }
  };

  const disconnectWallet = () => {
    setWalletAddress('');
  };

  const endGame = () => {
    setGameOver(true);
    setFinalScore(score);
    
    const isNewHigh = score > bestScore;
    setIsNewHighScore(isNewHigh);
    
    if (isNewHigh) {
      setBestScore(score);
      localStorage.setItem('best2048', score.toString());
    }
    
    setTimeout(() => setShowNFTModal(true), 500);
  };

  const mintGameNFT = async () => {
    if (!walletAddress) {
      alert('Please connect your wallet to mint your Game NFT!');
      return;
    }

    try {
      setIsMintingNFT(true);

      const nftMetadata = {
        name: isNewHighScore ? `2048 High Score: ${finalScore}` : `2048 Game Score: ${finalScore}`,
        description: isNewHighScore 
          ? `New High Score Achievement! Reached a high score of ${finalScore} in the fully on-chain 2048 game on Tempo Testnet. Every move was recorded as a transaction on Tempo - the blockchain designed for payments, powered by Stripe and Paradigm.`
          : `Game Completed on Tempo Testnet! Scored ${finalScore} points with ${moveCount} moves. All gameplay recorded on-chain on Tempo - the blockchain for payments.`,
        image: `https://tempo.xyz/og.png`,
        external_url: 'https://tempo.xyz',
        attributes: [
          {
            trait_type: 'Score',
            value: finalScore
          },
          {
            trait_type: 'Total Moves',
            value: moveCount
          },
          {
            trait_type: 'Achievement Type',
            value: isNewHighScore ? 'High Score' : 'Game Completion'
          },
          {
            trait_type: 'Game Date',
            value: new Date().toLocaleDateString()
          },
          {
            trait_type: 'Network',
            value: 'Tempo Testnet'
          },
          {
            trait_type: 'Gas Token',
            value: 'AlphaUSD'
          }
        ]
      };

      await new Promise(resolve => setTimeout(resolve, 2000));

      const nftTokenId = Math.floor(Math.random() * 1000000);
      const mintTxHash = '0x' + Math.random().toString(16).slice(2, 66);

      const nft = {
        tokenId: nftTokenId,
        metadata: nftMetadata,
        owner: walletAddress,
        mintedAt: new Date().toISOString(),
        txHash: mintTxHash,
        contractAddress: '0x' + Math.random().toString(16).slice(2, 42)
      };

      const updatedNFTs = [...mintedNFTs, nft];
      setMintedNFTs(updatedNFTs);
      localStorage.setItem('minted2048NFTs', JSON.stringify(updatedNFTs));

      setShowNFTModal(false);
      alert(`🎉 NFT Minted Successfully on Tempo!\n\nToken ID: #${nftTokenId}\nScore: ${finalScore}\nType: ${isNewHighScore ? 'High Score Achievement' : 'Game Completion'}\n\nCheck your wallet!`);
    } catch (error) {
      console.error('NFT minting failed:', error);
      alert('Failed to mint NFT. Please try again.');
    } finally {
      setIsMintingNFT(false);
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const addRandomTile = (g) => {
    const empty = [];
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (g[i][j] === 0) empty.push([i, j]);
      }
    }
    if (empty.length > 0) {
      const [row, col] = empty[Math.floor(Math.random() * empty.length)];
      g[row][col] = Math.random() < 0.9 ? 2 : 4;
    }
  };

  const simulateBackendTransaction = async (direction, newGrid, newScore) => {
    await new Promise(resolve => setTimeout(resolve, 100));

    const moveData = {
      gameId: gameId,
      moveNumber: moveCount + 1,
      direction: direction,
      score: newScore,
      timestamp: Date.now()
    };

    const txHash = '0x' + Math.random().toString(16).slice(2, 66);
    
    const transaction = {
      hash: txHash,
      blockNumber: 5234567 + moveCount,
      gasCost: '0.00001 AlphaUSD',
      timestamp: new Date().toLocaleTimeString(),
      data: moveData
    };

    setTransactionHistory(prev => [transaction, ...prev].slice(0, 10));
    return transaction;
  };

  const move = useCallback(async (dir) => {
    if (gameOver && !won) return;

    let moved = false;
    let newScore = score;
    const newGrid = grid.map(row => [...row]);

    const compress = (line) => {
      const filtered = line.filter(x => x !== 0);
      const result = [];
      let i = 0;
      while (i < filtered.length) {
        if (i + 1 < filtered.length && filtered[i] === filtered[i + 1]) {
          const merged = filtered[i] * 2;
          result.push(merged);
          newScore += merged;
          if (merged === 2048) {
            setWon(true);
          }
          i += 2;
          moved = true;
        } else {
          result.push(filtered[i]);
          i++;
        }
      }
      while (result.length < 4) result.push(0);
      return result;
    };

    if (dir === 'left') {
      for (let i = 0; i < 4; i++) {
        const orig = [...newGrid[i]];
        newGrid[i] = compress(newGrid[i]);
        if (!moved && JSON.stringify(orig) !== JSON.stringify(newGrid[i])) moved = true;
      }
    } else if (dir === 'right') {
      for (let i = 0; i < 4; i++) {
        const orig = [...newGrid[i]];
        newGrid[i] = compress(newGrid[i].reverse()).reverse();
        if (!moved && JSON.stringify(orig) !== JSON.stringify(newGrid[i])) moved = true;
      }
    } else if (dir === 'up') {
      for (let j = 0; j < 4; j++) {
        const col = [newGrid[0][j], newGrid[1][j], newGrid[2][j], newGrid[3][j]];
        const orig = [...col];
        const compressed = compress(col);
        for (let i = 0; i < 4; i++) newGrid[i][j] = compressed[i];
        if (!moved && JSON.stringify(orig) !== JSON.stringify(compressed)) moved = true;
      }
    } else if (dir === 'down') {
      for (let j = 0; j < 4; j++) {
        const col = [newGrid[0][j], newGrid[1][j], newGrid[2][j], newGrid[3][j]];
        const orig = [...col];
        const compressed = compress(col.reverse()).reverse();
        for (let i = 0; i < 4; i++) newGrid[i][j] = compressed[i];
        if (!moved && JSON.stringify(orig) !== JSON.stringify(compressed)) moved = true;
      }
    }

    if (moved) {
      await simulateBackendTransaction(dir, newGrid, newScore);
      
      addRandomTile(newGrid);
      setGrid(newGrid);
      setScore(newScore);
      setMoveCount(prev => prev + 1);
      
      if (newScore > bestScore) {
        setBestScore(newScore);
        localStorage.setItem('best2048', newScore.toString());
      }
      
      if (isGameOver(newGrid)) {
        setGameOver(true);
        setFinalScore(newScore);
        const isNewHigh = newScore > bestScore;
        setIsNewHighScore(isNewHigh);
        setTimeout(() => setShowNFTModal(true), 500);
      }
    }
  }, [grid, score, bestScore, gameOver, won, gameId, moveCount]);

  const isGameOver = (g) => {
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (g[i][j] === 0) return false;
        if (j < 3 && g[i][j] === g[i][j + 1]) return false;
        if (i < 3 && g[i][j] === g[i + 1][j]) return false;
      }
    }
    return true;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const dirMap = {
          ArrowUp: 'up',
          ArrowDown: 'down',
          ArrowLeft: 'left',
          ArrowRight: 'right'
        };
        move(dirMap[e.key]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [move]);

  const restart = () => {
    setGrid(initGrid());
    setScore(0);
    setGameOver(false);
    setWon(false);
    setGameId(Date.now().toString());
    setMoveCount(0);
    setTransactionHistory([]);
  };

  const getTileColor = (val) => {
    const colors = {
      0: 'bg-gray-200',
      2: 'bg-purple-100',
      4: 'bg-purple-200',
      8: 'bg-purple-300',
      16: 'bg-purple-400',
      32: 'bg-purple-500',
      64: 'bg-purple-600',
      128: 'bg-violet-400',
      256: 'bg-violet-500',
      512: 'bg-violet-600',
      1024: 'bg-indigo-600',
      2048: 'bg-indigo-700'
    };
    return colors[val] || 'bg-indigo-800';
  };

  const getTextColor = (val) => {
    return val > 4 ? 'text-white' : 'text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-100 flex items-center justify-center p-4">
      {showNFTModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="text-6xl mb-4">{isNewHighScore ? '🏆' : '🎮'}</div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {isNewHighScore ? 'New High Score!' : 'Game Complete!'}
              </h2>
              <p className="text-5xl font-bold text-purple-600 mb-4">{finalScore}</p>
              <p className="text-gray-600 mb-6">
                {isNewHighScore 
                  ? 'Congratulations! You beat your previous best! Mint an NFT to commemorate this achievement on Tempo Testnet.'
                  : `Great game! You scored ${finalScore} points. Mint an NFT to record this game on Tempo Testnet and increase your on-chain activity!`
                }
              </p>
              
              {!walletAddress ? (
                <div className="space-y-4">
                  <p className="text-orange-600 font-semibold">Connect your wallet to mint your game NFT!</p>
                  <button
                    onClick={connectWallet}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-bold transition"
                  >
                    Connect Wallet to Mint
                  </button>
                </div>
              ) : (
                <button
                  onClick={mintGameNFT}
                  disabled={isMintingNFT}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-4 rounded-lg font-bold transition disabled:opacity-50"
                >
                  {isMintingNFT ? 'Minting...' : '🎨 Mint Game NFT'}
                </button>
              )}
              
              <button
                onClick={() => setShowNFTModal(false)}
                className="w-full mt-3 text-gray-500 hover:text-gray-700 font-medium"
              >
                Maybe Later
              </button>
              
              {mintedNFTs.length > 0 && (
                <p className="text-sm text-gray-500 mt-4">
                  You've minted {mintedNFTs.length} NFT{mintedNFTs.length > 1 ? 's' : ''} • Building your Tempo footprint!
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl w-full">
        <div className="flex justify-end mb-4">
          {walletAddress ? (
            <div className="flex items-center gap-2">
              <div className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium">
                {formatAddress(walletAddress)}
              </div>
              <button
                onClick={disconnectWallet}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              disabled={isConnecting}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition disabled:opacity-50"
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet (Optional)'}
            </button>
          )}
        </div>

        <div className="text-center mb-4">
          <h1 className="text-6xl font-bold text-gray-800 mb-2">2048 On-Chain</h1>
          <p className="text-gray-600">Powered by Tempo - The blockchain for payments</p>
          <div className="mt-3 bg-green-100 border border-green-400 rounded-lg p-3 inline-block">
            <p className="text-green-800 font-medium">
              ✓ No wallet prompts - Play instantly!
            </p>
          </div>
          {mintedNFTs.length > 0 && (
            <div className="mt-3 bg-purple-100 border border-purple-400 rounded-lg p-3 inline-block">
              <p className="text-purple-800 font-medium">
                🎨 {mintedNFTs.length} NFT{mintedNFTs.length > 1 ? 's' : ''} Minted • Growing your Tempo footprint!
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mb-4 max-w-2xl mx-auto">
          <div className="flex gap-3">
            <div className="bg-purple-600 rounded-lg px-6 py-3">
              <div className="text-white text-xs font-bold uppercase">Score</div>
              <div className="text-white text-2xl font-bold">{score}</div>
            </div>
            <div className="bg-purple-600 rounded-lg px-6 py-3">
              <div className="text-white text-xs font-bold uppercase">Best</div>
              <div className="text-white text-2xl font-bold">{bestScore}</div>
            </div>
            <div className="bg-violet-600 rounded-lg px-6 py-3">
              <div className="text-white text-xs font-bold uppercase">Moves</div>
              <div className="text-white text-2xl font-bold">{moveCount}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={endGame}
              disabled={gameOver}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              End Game
            </button>
            <button
              onClick={restart}
              className="bg-gray-700 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-bold transition"
            >
              New Game
            </button>
          </div>
        </div>

        <div className="bg-purple-200 p-4 rounded-xl mb-4 relative max-w-2xl mx-auto">
          {(gameOver || won) && (
            <div className="absolute inset-0 bg-white/90 rounded-xl flex flex-col items-center justify-center z-10">
              <div className="text-4xl font-bold text-gray-800 mb-4">
                {won ? 'You Win!' : 'Game Over!'}
              </div>
              <div className="text-gray-600 mb-4">
                Final Score: {score} • {moveCount} moves • {transactionHistory.length} transactions
              </div>
              <button
                onClick={restart}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-bold transition"
              >
                Try Again
              </button>
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            {grid.map((row, i) =>
              row.map((cell, j) => (
                <div
                  key={`${i}-${j}`}
                  className={`${getTileColor(cell)} ${getTextColor(cell)} aspect-square rounded-lg flex items-center justify-center text-3xl font-bold transition-all duration-100`}
                >
                  {cell !== 0 && cell}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="text-center text-gray-600 text-sm max-w-2xl mx-auto">
          Use arrow keys to move tiles. Click "End Game" anytime to mint an NFT and build your Tempo testnet footprint!
        </div>
        
        <div className="text-center mt-6 text-gray-500 text-xs">
          2048 by Benny
        </div>
      </div>
    </div>
  );
}

export default App;
